// Structured rules replace an expression language: {field, op, value, intent}. They read the
// same in PHP (MaxRuleEvaluator) and here, and an approver can read them without code.
import type { Intent } from './tokens.ts'

export const RULE_OPS = ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'between', 'in', 'notIn', 'contains', 'empty', 'notEmpty'] as const
export type RuleOp = typeof RULE_OPS[number]

export type RuleScalar = string | number | boolean | null

export interface Rule {
  field: string
  op: RuleOp
  value?: RuleScalar | RuleScalar[]
  intent: Intent
  /** Apply the intent to the whole row instead of just the cell */
  scope?: 'cell' | 'row'
  label?: string
}

function toNumber (v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v)
  return null
}

function isEmpty (v: unknown): boolean {
  return v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0)
}

function looseEquals (a: unknown, b: unknown): boolean {
  const na = toNumber(a)
  const nb = toNumber(b)
  if (na !== null && nb !== null) return na === nb
  return String(a ?? '') === String(b ?? '')
}

export function ruleMatches (rule: Rule, row: Record<string, unknown>): boolean {
  const actual = row[rule.field]
  const expected = rule.value
  switch (rule.op) {
    case 'empty': return isEmpty(actual)
    case 'notEmpty': return !isEmpty(actual)
    case 'eq': return looseEquals(actual, expected)
    case 'neq': return !looseEquals(actual, expected)
    case 'in': return Array.isArray(expected) && expected.some(e => looseEquals(actual, e))
    case 'notIn': return Array.isArray(expected) && !expected.some(e => looseEquals(actual, e))
    case 'contains':
      return typeof expected === 'string' && String(actual ?? '').toLowerCase().includes(expected.toLowerCase())
    case 'between': {
      const n = toNumber(actual)
      if (n === null || !Array.isArray(expected) || expected.length !== 2) return false
      const lo = toNumber(expected[0])
      const hi = toNumber(expected[1])
      return lo !== null && hi !== null && n >= Math.min(lo, hi) && n <= Math.max(lo, hi)
    }
    default: {
      const n = toNumber(actual)
      const e = toNumber(expected)
      if (n === null || e === null) return false
      if (rule.op === 'gt') return n > e
      if (rule.op === 'gte') return n >= e
      if (rule.op === 'lt') return n < e
      if (rule.op === 'lte') return n <= e
      return false
    }
  }
}

/** First matching rule wins, so authors order rules from most to least specific. */
export function evaluateRules (rules: Rule[] | undefined, row: Record<string, unknown>, field?: string): Rule | null {
  if (!rules?.length) return null
  for (const rule of rules) {
    if (field !== undefined && (rule.scope ?? 'cell') === 'cell' && rule.field !== field) continue
    if (ruleMatches(rule, row)) return rule
  }
  return null
}

export function rowIntent (rules: Rule[] | undefined, row: Record<string, unknown>): Intent | null {
  const rowRules = rules?.filter(r => r.scope === 'row')
  return evaluateRules(rowRules, row)?.intent ?? null
}

export function cellIntent (rules: Rule[] | undefined, row: Record<string, unknown>, field: string): Intent | null {
  const cellRules = rules?.filter(r => (r.scope ?? 'cell') === 'cell' && r.field === field)
  return evaluateRules(cellRules, row)?.intent ?? null
}

/** Plain-language rendering for the approval screen, e.g. "margin is below 0.1 → negative". */
export function describeRule (rule: Rule): string {
  const v = Array.isArray(rule.value) ? rule.value.join(', ') : String(rule.value ?? '')
  const ops: Record<RuleOp, string> = {
    eq: `is ${v}`,
    neq: `is not ${v}`,
    gt: `is above ${v}`,
    gte: `is at least ${v}`,
    lt: `is below ${v}`,
    lte: `is at most ${v}`,
    between: `is between ${v.replace(', ', ' and ')}`,
    in: `is one of ${v}`,
    notIn: `is none of ${v}`,
    contains: `contains "${v}"`,
    empty: 'is empty',
    notEmpty: 'is not empty'
  }
  return `${rule.field} ${ops[rule.op]} → ${rule.intent}${rule.scope === 'row' ? ' (whole row)' : ''}`
}
